<template>
  <b-modal
    ref="detail-modal"
    title="Chi tiết thay đổi"
    size="xl"
    hide-footer
    scrollable
  >
    <div v-if="onLoading" class="text-center py-4">
      <b-spinner small variant="primary" />
    </div>
    <template v-else-if="detail">
      <dl class="row mb-3">
        <dt class="col-5 col-md-3 col-lg-2">Bộ phụ kiện</dt>
        <dd class="col-7 col-md-9 col-lg-10 text-break">
          {{ detail.programName }}
          <span
            v-if="detail.isProgramDeleted"
            class="label label-light-dark label-inline ml-1"
            >Đã xóa</span
          >
        </dd>
        <dt class="col-5 col-md-3 col-lg-2">Hành động</dt>
        <dd class="col-7 col-md-9 col-lg-10">{{ actionLabel }}</dd>
        <dt class="col-5 col-md-3 col-lg-2">Người thực hiện</dt>
        <dd class="col-7 col-md-9 col-lg-10">{{ detail.updatedByName || '—' }}</dd>
        <dt class="col-5 col-md-3 col-lg-2">Thời gian</dt>
        <dd class="col-7 col-md-9 col-lg-10">{{ formatTime(detail.createdAt) }}</dd>
      </dl>

      <p v-if="isDelete" class="font-weight-bold mb-0">
        {{ messages.PROGRAM_DELETED }}
      </p>
      <template v-else>
        <template v-if="detail.diffValues.length">
          <h6 class="font-weight-bold">Thông tin chung</h6>
          <b-table
            :items="detail.diffValues"
            :fields="headerFields"
            bordered
            small
            responsive
            class="mb-4"
          >
            <template #cell(originalValue)="{ value }">
              <span class="text-break">{{ value || '' }}</span>
            </template>
            <template #cell(newValue)="{ value }">
              <span class="text-break">{{ value || '' }}</span>
            </template>
          </b-table>
        </template>

        <template v-for="section in sectionRows">
          <h6 :key="`${section.key}-title`" class="font-weight-bold mt-3">
            {{ section.title }}
          </h6>
          <b-table
            :key="`${section.key}-table`"
            :items="section.rows"
            :fields="section.fields"
            :per-page="PAGE_SIZE"
            :current-page="pages[section.key]"
            bordered
            small
            responsive
            class="mb-2"
          >
            <template #cell(name)="{ value }">
              <span class="text-break">{{ value }}</span>
            </template>
          </b-table>
          <b-pagination
            v-if="section.rows.length > PAGE_SIZE"
            :key="`${section.key}-paging`"
            v-model="pages[section.key]"
            :total-rows="section.rows.length"
            :per-page="PAGE_SIZE"
            size="sm"
            align="right"
          />
        </template>
      </template>
    </template>
  </b-modal>
</template>

<script>
import moment from 'moment';
import ApiService from '@/core/services/api.service';
import { makeToastFaile } from '@/utils/common';
import {
  ACCESSORY_HISTORY_SOURCE,
  PROMOTION_ACCESSORY_HISTORY_MESSAGES,
} from '@/utils/constants';
import { HISTORY_ACTION_TYPE } from '@/utils/enum';

const PAGE_SIZE = 50;
const FORBIDDEN = 403;

const ACTION_LABEL = {
  [HISTORY_ACTION_TYPE.CREATE]: 'Tạo bộ phụ kiện',
  [HISTORY_ACTION_TYPE.UPDATE]: 'Sửa bộ phụ kiện',
  [HISTORY_ACTION_TYPE.DELETE]: 'Xóa bộ phụ kiện',
};

// "Trước → Sau" khi giá trị đổi, ngược lại chỉ hiện giá trị hiện tại
const toChange = (prev, next) => (prev !== next ? `${prev || ''} → ${next || ''}` : next);

const withChange = (group = {}, mapUpdated = (item) => item) => [
  ...(group.added || []).map((item) => ({ ...item, change: 'Thêm' })),
  ...(group.updated || []).map((item) => ({ ...mapUpdated(item), change: 'Sửa' })),
  ...(group.removed || []).map((item) => ({ ...item, change: 'Xóa' })),
];

// Dòng Sửa của SP có giá: "Trước → Sau" cho chiết khấu / giá
const withPriceChange = (item) => ({
  ...item,
  discountText: toChange(item.prevDiscountText, item.discountText),
  priceText: toChange(item.prevPriceText, item.priceText),
});

const CHANGE_FIELD = { key: 'change', label: 'Thay đổi', thStyle: { width: '10%' } };
const LIST_FIELDS = [
  CHANGE_FIELD,
  { key: 'typeName', label: 'Loại', thStyle: { width: '12%' } },
  { key: 'code', label: 'Mã', tdClass: 'text-nowrap' },
  { key: 'name', label: 'Tên' },
];

// `groups`: các nhóm listChanges gộp vào 1 bảng (danh mục + sản phẩm, cột Loại phân biệt); `priced`: nhóm có giá.
const SECTIONS = {
  PROMOTION: [
    {
      key: 'products',
      title: 'Sản phẩm bán kèm',
      groups: ['products'],
      priced: true,
      fields: [
        CHANGE_FIELD,
        { key: 'code', label: 'Mã SP', tdClass: 'text-nowrap' },
        { key: 'name', label: 'Tên SP' },
        { key: 'discountText', label: 'Chiết khấu', tdClass: 'text-nowrap' },
        { key: 'priceText', label: 'Giá bán sau chiết khấu', tdClass: 'text-nowrap' },
      ],
    },
    {
      key: 'applied',
      title: 'Bộ phụ kiện cấu hình cho',
      groups: ['appliedCategories', 'appliedProducts'],
      fields: LIST_FIELDS,
    },
  ],
  ATTACH: [
    {
      key: 'gifts',
      title: 'Sản phẩm tặng kèm',
      groups: ['gifts'],
      priced: true,
      fields: [
        CHANGE_FIELD,
        { key: 'code', label: 'Mã SP', tdClass: 'text-nowrap' },
        { key: 'name', label: 'Tên SP' },
        { key: 'priceText', label: 'Giá thu lại', tdClass: 'text-nowrap' },
      ],
    },
    {
      key: 'config',
      title: 'Bộ phụ kiện cấu hình cho',
      groups: ['categories', 'products'],
      fields: LIST_FIELDS,
    },
    {
      key: 'applied',
      title: 'Sản phẩm/ nhóm sản phẩm áp dụng',
      groups: ['appliedCategories', 'appliedProducts'],
      fields: LIST_FIELDS,
    },
  ],
};

const initPages = (source) =>
  SECTIONS[source].reduce((pages, section) => ({ ...pages, [section.key]: 1 }), {});

export default {
  name: 'PromotionAccessoryHistoryDetail',
  props: {
    source: {
      type: String,
      default: 'PROMOTION',
      validator: (value) => value in ACCESSORY_HISTORY_SOURCE,
    },
  },
  data() {
    return {
      PAGE_SIZE,
      messages: PROMOTION_ACCESSORY_HISTORY_MESSAGES,
      detail: null,
      onLoading: false,
      pages: initPages(this.source),
      headerFields: [
        { key: 'keyName', label: 'Thông tin', thStyle: { width: '30%' } },
        { key: 'originalValue', label: 'Trước' },
        { key: 'newValue', label: 'Sau' },
      ],
    };
  },
  computed: {
    isDelete() {
      return this.detail && this.detail.action === HISTORY_ACTION_TYPE.DELETE;
    },
    actionLabel() {
      return this.detail ? ACTION_LABEL[this.detail.action] || '' : '';
    },
    sectionRows() {
      if (!this.detail || !this.detail.listChanges) return [];
      const { listChanges } = this.detail;
      return SECTIONS[this.source]
        .map((section) => ({
          ...section,
          rows: section.groups.flatMap((group) =>
            withChange(listChanges[group], section.priced ? withPriceChange : undefined),
          ),
        }))
        .filter((section) => section.rows.length);
    },
  },
  methods: {
    formatTime(value) {
      return value ? moment(value).format('HH:mm DD/MM/YYYY') : '';
    },
    async show(historyId) {
      this.detail = null;
      this.pages = initPages(this.source);
      this.$refs['detail-modal'].show();
      this.onLoading = true;
      try {
        const { endpoint } = ACCESSORY_HISTORY_SOURCE[this.source];
        const { data } = await ApiService.get(`${endpoint}/${historyId}`);
        this.detail = data.data;
      } catch (error) {
        const status = error && error.response && error.response.status;
        makeToastFaile(
          status === FORBIDDEN
            ? ACCESSORY_HISTORY_SOURCE[this.source].forbiddenMessage
            : PROMOTION_ACCESSORY_HISTORY_MESSAGES.LOAD_FAILED,
        );
        this.$refs['detail-modal'].hide();
      } finally {
        this.onLoading = false;
      }
    },
  },
};
</script>
